// themes/labelsimply/spa/src/api/frontend.ts

import { apiClient } from "@/api/axios.ts"
import type { ProductResponse } from "@/api/types.ts"
import type { AxiosResponse } from "axios";

export interface DesignerConfig {
  width_mm: number
  height_mm: number
  canvasSizeInMm: { width: number; height: number }
  labelImage?: {
    imageSrc: string
    position: { x: number; y: number }
    dimensions: { width: number; height: number }
    fit: 'cover' | 'contain'
  }
}

export interface CartItemPayload {
  product_id: number
  designerConfig: DesignerConfig
  material: 'paper' | 'plastic'
  quantity: number
}

export interface CartItemResponse {
  id: number
  product_id: number
  sticker_quantity: number
  material: string
  unit_price: number
  total_price: number
  sheet_count: number
  designer_config?: DesignerConfig
}

export async function getPublicProductBySlug(slug: string): Promise<ProductResponse> {
  const { data } = await apiClient(false).get<ProductResponse>(`/product/slug/${encodeURIComponent(slug)}`)
  return data
}

export async function calculateLabelPrice(
  designerConfig: DesignerConfig,
  material: 'paper' | 'plastic',
  quantity: number,
) {
  const payload = {
    product_id: 123, // TODO: replace dynamically
    designer_config: designerConfig,
    material,
    quantity,
  }

  const { data } = await apiClient(false).post('/calculate-price', payload)
  return data.price_calculation
}

export async function createCartItem(payload: CartItemPayload): Promise<CartItemResponse> {
  const postPayload = {
    product_id: payload.product_id,
    designer_config: payload.designerConfig,
    material: payload.material,
    quantity: payload.quantity,
  }

  const { data } = await apiClient(false).post('/cart-item', postPayload)
  return data.cart_item
}

export async function fetchCartItem(id: number): Promise<CartItemResponse> {
  const { data } = await apiClient(false).get(`/cart-item/${id}`)
  return data
}

export interface CartDto {
  currency: string
  discounts: any[]
  id: number
  cart_items: CartItemResponse[]
  total_items: number
  total_price: number
}
export interface CartResponse {
  available_payment_methods: any[]
  available_shipping_methods: any[]
  cart: CartDto;

}
export async function updateCartItem(id: number, payload: Partial<Omit<CartItemPayload, 'product_id'>>): Promise<void> {
  const postPayload: Record<string, any> = {}
  if (payload.designerConfig) postPayload.designer_config = payload.designerConfig
  if (payload.material) postPayload.material = payload.material
  if (payload.quantity) postPayload.quantity = payload.quantity

  return apiClient(false).post(`/cart-item/${id}/update`, postPayload).then(res => res.data)
}

export async function getCart(): Promise<CartResponse> {
  return apiClient(false).get('/cart').then((response: AxiosResponse<CartResponse>) =>
    Promise.resolve(response.data)
  )
}

export async function getPricingConfig() {
  const { data } = await apiClient(false).get('pricing-config')
  return data as {
    sra3_size_mm: [number, number],
    material_prices: Record<'paper' | 'plastic' | string, number>,
    min_archs: number
  }
}

export async function uploadCustomImage(file: File): Promise<{
  id: number
  url: string
  filename: string
  size: number
}> {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await apiClient(false).post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return data
}

export async function getCustomImage(id: number): Promise<{
  id: number
  url: string
  filename: string
}> {
  const { data } = await apiClient(false).get(`/custom-image/${id}`)
  return data
}
